import moment from 'moment';
import { Customizations, ImageObject, TemplateDetails } from './template-details.model';
import { AttendeeSummary } from './CampaignRecipients.model';

export interface CampaignDetails {
  audienceID?: string;
  templateID?: string;
  type: string;
  status: CampaignStatus;
  id: string;
  name: string;
  recipients: string;
  createdBy: string;
  createdAt: moment.Moment;
  updatedBy?: string;
  updatedAt?: moment.Moment;
  notifications: Notification[];
  event?: EventDetails;
  reward?: RewardDetails;
}

export interface HighMessagingDetails {
  customer_count: number;
}

export enum CampaignStatus {
  Scheduled = 'SCHEDULED',
  InProgress = 'IN_PROGRESS',
  Cancelled = 'CANCELLED',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
}

export enum CampaignType {
  Reward = 'REWARD',
  Event = 'EVENT',
  Notification = 'NOTIFICATION',
  Automation = 'AUTOMATION'
}

export function convertToEnum<T extends Record<string, string>>(type: T, value: string): T[keyof T] {
  if (Object.values(type).includes(value)) {
    return value as T[keyof T];
  }
  throw new Error('Campaign type unknown.')
}

export interface Notification {
  id: string;
  creationStatus: CampaignStatus;
  sendDatetime: moment.Moment;
  templateID?: number;
  layoutID?: string
  segment: string;
  channel: string;
  emailCustomizations: {
    subject: string;
    themeID: string;
    body: string;
    recruitmentUrl?: string;
    heroImage?: ImageObject
  };
}

export interface RewardDetails {
  startDatetime: moment.Moment;
  endDatetime: moment.Moment;
  maxDaysAllowed?: number;
  failedCustomers?: string;
  offerBatchID?: string;
  appMessage: string;
  includeOperatorImage: boolean;
  isLimitedTimeOffer?: boolean;
  rewardID: string;
  name?: string;
  durationDays?: number;
  includeActiveLocationRewardCustomers: boolean;
  deletedAt?: moment.Moment
}

export interface EventDetails {
  venue: string;
  startDate: moment.Moment;
  endDate: moment.Moment;
  startTime: string;
  endTime: string;
  rsvpRequired: boolean;
  limitedReservations: boolean;
  limitedAttendeesPerCustomer: boolean;
  adultsOnly: boolean;
  maxReservations?: number;
  maxAttendeesPerCustomer?: number;
  remindRSVPed: boolean;
  remindNotRSVPed: boolean;
  sendCancellationNotification?: boolean;
  sendUpdateNotification?: boolean;
  sendReminderEmails: boolean;
  subject?: string;
  body?: string;
}

export interface EventAttendee {
  cfaId: string;
  adultsAttending: number;
  kidsAttending: number;
}

export interface CampaignRecipientStatuses {
  campaignID?: string,
  campaignRecipients?: string[],
  emailStatuses: {
    sent: Set<string>,
    delivered: Set<string>,
    failed: Set<string>,
    failedProcessing: Set<string>,
    dropped: Set<string>,
    opened: Set<string>,
    spam: Set<string>,
    unsubscribed: Set<string>,
  },
  rewardStatuses: {
    locationRewardExclusion: Set<string>,
    duplicateRewardExclusion: Set<string>,
    rewardCreationError: Set<string>,
    loaded: Set<string>,
    redeemedAtIssuedLocation: Set<string>,
    redeemedAtOtherLocation: Set<string>,
  }
  eventRSVPSummary: {
    going: EventAttendee[],
    notGoing: Set<string>,
    maybe: Set<string>,
  }
  attendeeSummary: AttendeeSummary;
}

export interface SendingOptions{
    sendImmediate: boolean;
    sendDate: moment.Moment;
    sendTime: string;
    groupNow: boolean
}

export interface CampaignInputs {
  customizations: Customizations,
  rewardInformation: RewardDetails,
  template: TemplateDetails,
  sendingOptions: SendingOptions,
  eventInformation?: EventDetails,
  cfaIDs: string[],
  audienceID?: string,
  campaignID? : string,
  notificationID?: string,
  goal?: string
}
